import { initializeApp, getApps, getApp, deleteApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import type { Messaging } from 'firebase/messaging';

// Runtime Firebase config — fetched from the server at startup so each workspace
// can have its own Firebase project. Falls back to VITE_FIREBASE_* build-time
// vars when the server returns {configured: false} (e.g. elmessagehub not wired).

interface FirebaseRuntimeConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  vapidKey: string;
}

let resolvedConfig: FirebaseRuntimeConfig | null = null;
let configFetched = false;

function buildtimeConfig(): FirebaseRuntimeConfig | null {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY as string | undefined;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined;
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;
  if (!apiKey || !projectId || !vapidKey) return null;
  return {
    apiKey,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string ?? '',
    projectId,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string ?? '',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string ?? '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID as string ?? '',
    vapidKey,
  };
}

function getWorkspaceId(): string {
  const match = document.cookie.match(/(?:^|;\s*)elauth_workspace_id=([^;]*)/)
  return match?.[1] ? decodeURIComponent(match[1]) : ''
}

// fetchRuntimeConfig contacts the elbooks backend for the per-workspace Firebase
// web SDK config. Called once; result is cached in module scope.
async function fetchRuntimeConfig(): Promise<FirebaseRuntimeConfig | null> {
  if (configFetched) return resolvedConfig;
  configFetched = true;

  const workspaceId = getWorkspaceId();
  if (!workspaceId) {
    resolvedConfig = buildtimeConfig();
    return resolvedConfig;
  }

  try {
    const resp = await fetch(
      `/api/v1/notifications/firebase-config?workspace_id=${encodeURIComponent(workspaceId)}`,
    );
    if (!resp.ok) {
      resolvedConfig = buildtimeConfig();
      return resolvedConfig;
    }
    const data = await resp.json() as {
      configured?: boolean;
      apiKey?: string;
      authDomain?: string;
      projectId?: string;
      storageBucket?: string;
      messagingSenderId?: string;
      appId?: string;
      vapidKey?: string;
    };
    if (data.configured && data.apiKey && data.projectId && data.vapidKey) {
      resolvedConfig = {
        apiKey: data.apiKey,
        authDomain: data.authDomain ?? '',
        projectId: data.projectId,
        storageBucket: data.storageBucket ?? '',
        messagingSenderId: data.messagingSenderId ?? '',
        appId: data.appId ?? '',
        vapidKey: data.vapidKey,
      };
    } else {
      resolvedConfig = buildtimeConfig();
    }
  } catch {
    resolvedConfig = buildtimeConfig();
  }
  return resolvedConfig;
}

let messaging: Messaging | null = null;

// getMessagingInstance returns the Firebase Messaging instance for the resolved
// config. Reinitialises if the config changed (workspace switch).
async function getMessagingInstance(): Promise<Messaging | null> {
  const cfg = await fetchRuntimeConfig();
  if (!cfg) return null;
  try {
    // If an app is already initialised for a different project, tear it down.
    const existingApps = getApps();
    const existingApp = existingApps[0];
    if (existingApp && existingApp.options.projectId !== cfg.projectId) {
      await deleteApp(existingApp);
      messaging = null;
    }
    const app = getApps().length ? getApp() : initializeApp(cfg);
    if (!messaging) messaging = getMessaging(app);
    return messaging;
  } catch {
    return null;
  }
}

// requestPermissionAndGetToken asks the browser for notification permission,
// registers the service worker, and returns the FCM registration token.
// Returns null when Firebase is not configured, permission denied, or SW unavailable.
export async function requestPermissionAndGetToken(): Promise<string | null> {
  if (!('serviceWorker' in navigator) || !('Notification' in window)) return null;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const msg = await getMessagingInstance();
  if (!msg) return null;

  const cfg = await fetchRuntimeConfig();
  if (!cfg) return null;

  try {
    // Pass the runtime config to the service worker so it can initialise Firebase
    // with the same project, even without build-time env vars baked in.
    const sw = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/',
    });
    // Tell the SW the runtime config so onBackgroundMessage uses the right project.
    if (sw.active) {
      sw.active.postMessage({ type: 'FIREBASE_CONFIG', config: cfg });
    }
    const token = await getToken(msg, {
      vapidKey: cfg.vapidKey,
      serviceWorkerRegistration: sw,
    });
    return token || null;
  } catch {
    return null;
  }
}

// getCurrentToken returns the existing FCM token without requesting permission.
export async function getCurrentToken(): Promise<string | null> {
  if (!('serviceWorker' in navigator)) return null;
  const msg = await getMessagingInstance();
  if (!msg) return null;
  const cfg = await fetchRuntimeConfig();
  if (!cfg) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/');
    if (!reg) return null;
    return await getToken(msg, { vapidKey: cfg.vapidKey, serviceWorkerRegistration: reg });
  } catch {
    return null;
  }
}

// onForegroundMessage calls handler whenever a FCM message arrives while the
// tab is in the foreground. Returns an unsubscribe function.
export function onForegroundMessage(
  handler: (payload: { notification?: { title?: string; body?: string } }) => void,
): () => void {
  // We need a synchronous return, so we eagerly initialise and fall back to no-op.
  let unsub: (() => void) | null = null;
  void getMessagingInstance().then(msg => {
    if (!msg) return;
    unsub = onMessage(msg, handler);
  });
  return () => { unsub?.(); };
}
